<template>
  <div class="scheme-nodes-tree">
    <v-container>
      <div class="d-flex justify-space-between align-center mb-4">
        <h2>Узлы схемы</h2>
        <v-btn small text :disabled="loading" @click="openCreateDialog(null)">
          <v-icon left small>mdi-plus-thick</v-icon>
          Добавить корневой узел
        </v-btn>
      </div>

      <v-treeview
        v-if="tree.length"
        :items="tree"
        item-key="id"
        open-all
        dense
        @mousemove.native="onTreeMouseMove"
        @mouseleave.native="hidePreview"
        @click.native="hidePreview"
      >
        <template v-slot:prepend="{ item, open }">
          <!-- Одна строка: значок публикации слева от превью. Место под значок есть у всех узлов, чтобы превью
               стояли ровно; контейнер prepend у v-treeview — блок, без flex значок и картинка встают друг под другом -->
          <div class="node-prepend">
            <span v-if="!item.is_folder" class="node-publish-slot">
              <v-icon
                v-if="!item.is_published"
                x-small
                title="Не опубликован — недоступен при построении схемы"
              >
                mdi-eye-off-outline
              </v-icon>
            </span>
            <!-- data-node-preview: по наведению на саму миниатюру показывается увеличенное превью (onTreeMouseMove) -->
            <v-img
              v-if="!item.is_folder && item._file && item._file.url"
              :src="item._file.url"
              :data-node-preview="item._file.url"
              contain
              width="24"
              height="24"
            />
            <v-icon v-else :color="item.is_folder ? item.color : undefined">
              {{
                item.is_folder
                  ? open
                    ? "mdi-folder-open"
                    : "mdi-folder"
                  : "mdi-cog-outline"
              }}
            </v-icon>
          </div>
        </template>

        <template v-slot:label="{ item }">
          <div class="node-label-row" @click="openEditDialog(item)">
            <div
              class="node-label-box"
              :class="{ 'node-label-box--colored': item.color }"
              :style="item.color ? { borderColor: item.color } : {}"
            >
              <span class="font-weight-medium">{{ item.code }}</span>
              <span v-if="item.name_ru" class="grey--text ml-2">{{
                item.name_ru
              }}</span>
              <span v-else-if="item.description_ru" class="grey--text ml-2">{{
                item.description_ru
              }}</span>
            </div>
            <span class="node-label-leader"></span>
          </div>
        </template>

        <template v-slot:append="{ item }">
          <div class="d-flex align-center" style="grid-column-gap: 5px">
            <IconTooltip
              v-if="!item.id_file"
              icon-text="mdi-plus-thick"
              text-tooltip="Добавить дочерний узел"
              color-icon="green"
              :is-disabled="loading"
              @click-icon="openCreateDialog(item)"
            />
            <IconTooltip
              icon-text="mdi-pencil"
              text-tooltip="Редактировать узел"
              :is-disabled="loading"
              @click-icon="openEditDialog(item)"
            />
            <IconTooltip
              icon-text="mdi-delete-outline"
              :text-tooltip="
                hasChildren(item)
                  ? 'Нельзя удалить узел с детьми'
                  : 'Удалить узел'
              "
              color-icon="red"
              :is-disabled="hasChildren(item) || loading"
              @click-icon="openDeleteDialog(item)"
            />
          </div>
        </template>
      </v-treeview>

      <v-alert v-else-if="!loading" type="info" text class="mt-4">
        Узлов пока нет
      </v-alert>
    </v-container>

    <!-- Увеличенное превью узла при наведении на его миниатюру в дереве: следует за курсором, кликам не мешает -->
    <div
      v-if="hoverPreview.url"
      class="node-hover-preview"
      :style="{ left: hoverPreview.x + 'px', top: hoverPreview.y + 'px' }"
    >
      <img :src="hoverPreview.url" alt="" />
    </div>

    <input
      ref="fileInput"
      type="file"
      accept=".svg,image/svg+xml"
      style="display: none"
      @change="onFileSelected"
    />

    <!-- Диалог создания / редактирования узла -->
    <v-dialog
      v-model="dialog"
      width="70%"
      scrollable
      content-class="node-dialog"
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>{{
            isEditing
              ? `Редактирование узла — ${entry.code}`
              : "Создание узла"
          }}</span>
          <v-spacer />
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field
            ref="codeField"
            v-model="entry.code"
            label="Код"
            :rules="requiredFieldRules"
            outlined
            dense
            hide-details="auto"
          />
          <div v-if="parentCode" class="grey--text">
            Родитель: {{ parentCode }}
          </div>

          <!-- Публикация: опубликованный узел можно выбрать при построении схемы; в админке и в правилах
               соответствия узел виден всегда, в том числе неопубликованный -->
          <div v-if="!entry.is_folder" class="mt-3 d-flex align-center">
            <v-switch
              v-model="entry.is_published"
              :disabled="!entry.id_file"
              label="Опубликован"
              color="success"
              inset
              dense
              hide-details
              class="mt-0 flex-grow-0"
            />
            <!-- справка вместо подписи под переключателем -->
            <v-tooltip bottom max-width="340" content-class="node-dialog-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-2" v-bind="attrs" v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>
                Опубликованный узел можно выбрать при построении схемы. Неопубликованный виден только
                в админке и в правилах.
                <template v-if="!entry.id_file">Опубликовать можно после загрузки SVG.</template>
              </span>
            </v-tooltip>
          </div>

          <!-- Название узла: заголовок его публичной страницы (/schemes/nodes/<код>), у папок название задаётся в «Легенде категории» -->
          <v-text-field
            v-if="!entry.is_folder"
            v-model="entry.name_ru"
            label="Название узла, RU"
            outlined
            dense
            hide-details
            class="mt-4"
          />

          <!-- Где узел используется: правки чертежа и удаление затрагивают все эти схемы -->
          <v-alert
            v-if="isEditing && !entry.is_folder && usage"
            :type="usage.schemes.length ? 'warning' : 'info'"
            text
            dense
            outlined
            class="mt-4 mb-0 text-body-2"
          >
            <template v-if="usage.schemes.length">
              <div class="d-flex align-center">
                <div class="font-weight-medium">
                  Используется в схемах: {{ usage.schemes.length }}
                  <span v-if="usage.frozen_slots">
                    · куплено/установлено слотов: {{ usage.frozen_slots }} — чертёж заменить нельзя
                  </span>
                </div>
                <v-spacer />
                <v-btn
                  icon
                  x-small
                  :title="usageOpen ? 'Скрыть список схем' : 'Показать список схем'"
                  @click="toggleUsage"
                >
                  <v-icon>{{ usageOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                </v-btn>
              </div>
              <!-- v-show на контейнере: у строк ниже d-flex (display:flex !important) перебил бы display:none -->
              <div v-show="usageOpen">
              <div class="text-caption mt-1 mb-2">
                Узел с этим использованием удалить нельзя. Замена SVG сдвигает нумерацию
                оборудования (КШ-1, КШ-2…) в этих схемах.
              </div>
              <div
                v-for="scheme in usage.schemes"
                :key="scheme.id"
                class="d-flex align-center flex-wrap"
                style="grid-column-gap: 8px"
              >
                <a
                  :href="schemeLink(scheme)"
                  target="_blank"
                  rel="noopener"
                  class="usage-scheme-link"
                  >«{{ scheme.name }}» #{{ scheme.id }}</a
                >
                <v-chip x-small label>{{ scheme.type === "base" ? "базовая" : "пользовательская" }}</v-chip>
                <span v-if="scheme.placements > 1" class="text-caption">
                  размещён {{ scheme.placements }} раза
                </span>
                <span
                  v-if="scheme.slots.draft + scheme.slots.purchased + scheme.slots.installed"
                  class="text-caption"
                >
                  слоты: наброски {{ scheme.slots.draft }}, куплено {{ scheme.slots.purchased }},
                  установлено {{ scheme.slots.installed }}
                </span>
              </div>
              </div>
            </template>
            <template v-else>Ни в одной схеме не используется — можно менять и удалять.</template>
          </v-alert>

          <v-expansion-panels
            v-model="notePanelIndex"
            flat
            multiple
            class="mt-4"
          >
            <v-expansion-panel
              style="border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px"
            >
              <v-expansion-panel-header class="text-caption text--secondary">
                Примечание
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-textarea
                  v-model="entry.description_ru"
                  label="Примечание, RU"
                  outlined
                  dense
                  rows="2"
                  hide-details
                  class="mb-4"
                />
                <v-textarea
                  v-model="entry.description_en"
                  label="Примечание, EN"
                  outlined
                  dense
                  rows="2"
                  hide-details
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- SEO публичной страницы узла: только у узлов (не у папок) -->
          <v-expansion-panels
            v-if="!entry.is_folder"
            v-model="seoPanelIndex"
            flat
            multiple
            class="mt-4"
          >
            <v-expansion-panel
              style="border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px"
            >
              <v-expansion-panel-header class="text-caption text--secondary">
                SEO страницы узла
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-text-field
                  v-model="entry.seo_title"
                  label="SEO title"
                  counter="255"
                  outlined
                  dense
                  class="mb-2"
                />
                <v-textarea
                  v-model="entry.seo_description"
                  label="SEO description"
                  outlined
                  dense
                  rows="2"
                  hide-details
                  class="mb-4"
                />
                <v-text-field
                  v-model="entry.seo_keywords"
                  label="SEO keywords"
                  outlined
                  dense
                  hide-details
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div v-if="isEditing && entry.is_folder" class="mt-4">
            <div class="grey--text text--darken-1 mb-2">Легенда категории</div>
            <v-text-field
              v-model="entry.name_ru"
              label="Название, RU"
              outlined
              dense
              hide-details
              class="mb-4"
            />
            <v-text-field
              v-model="entry.name_en"
              label="Название, EN"
              outlined
              dense
              hide-details
              class="mb-3"
            />
            <div class="d-flex align-center" style="grid-column-gap: 12px">
              <input
                v-model="entry.color"
                type="color"
                style="width: 40px; height: 40px; border: none; padding: 0; cursor: pointer"
              />
              <v-text-field
                v-model="entry.color"
                label="Цвет (hex)"
                outlined
                dense
                hide-details
              />
            </div>
          </div>

          <v-expansion-panels
            v-if="isEditing && !entry.is_folder"
            flat
            multiple
            v-model="expandedPanels"
            class="mt-4"
          >
            <v-expansion-panel
              style="border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px"
            >
              <v-expansion-panel-header class="text-caption text--secondary">
                SVG, коннекторы и оборудование
              </v-expansion-panel-header>
              <v-expansion-panel-content>
            <!-- Две колонки: слева превью SVG, справа управление и описание
                 (справа от картинки свободное место). Если диалог слишком узкий,
                 правая колонка переносится под превью. -->
            <div ref="panelCols" class="d-flex" style="gap: 24px; align-items: flex-start; flex-wrap: wrap">
              <div
                v-if="entry._file && entry._file.url && (svgViewBoxLoading || svgViewBox)"
                style="flex: 0 0 auto"
              >
                <!-- пока viewBox не готов — лоадер на том же месте, где потом
                     появится пикер (сырую картинку без точек не показываем,
                     чтобы не путать раскладку и не "мигать" разными версиями) -->
                <div
                  v-if="entry._file && entry._file.url && svgViewBoxLoading"
                  class="mb-3 d-flex align-center justify-center"
                  :style="anchorSvgPlaceholderStyle"
                >
                  <v-progress-circular indeterminate color="primary" />
                </div>

                <div
                  v-if="entry._file && entry._file.url && svgViewBox"
                  class="mb-3"
                >
                  <!-- Размер svg (anchorSvgStyle) можно менять свободно: рамки элементов
                       и подложка заданы в единицах viewBox, клики ловятся на самих
                       элементах, а браузер вписывает картинку целиком (letterbox) -->
                  <svg
                    ref="anchorSvg"
                    :viewBox="anchorViewBox"
                    :style="anchorSvgStyle"
                  >
                    <!-- белая подложка ровно по границам viewBox — визуально
                         отделяет саму картинку от пустых letterbox-полей по бокам -->
                    <rect
                      :x="svgViewBox.minX"
                      :y="svgViewBox.minY"
                      :width="svgViewBox.w"
                      :height="svgViewBox.h"
                      fill="white"
                    />
                    <image
                      :href="anchorImageUrl"
                      :x="svgViewBox.minX"
                      :y="svgViewBox.minY"
                      :width="svgViewBox.w"
                      :height="svgViewBox.h"
                    />
                    <!-- Трубы справочника, найденные на SVG: прозрачная зона наведения по линиям
                         трубы и подсветка при наведении — с картинки или со строки таблицы
                         «Коннекторы», как у оборудования. Толщина линий в пикселях экрана
                         (non-scaling-stroke), а не в единицах viewBox, поэтому одинакова на любой
                         схеме. Слой ниже рамок элементов — наведение на кран важнее трубы под ним. -->
                    <g
                      v-for="pipe in pipeOverlays"
                      :key="`pipe-${pipe.code}`"
                      @mouseenter="setHoveredPipe(pipe.code, true)"
                      @mouseleave="setHoveredPipe(null)"
                    >
                      <polyline
                        v-for="(points, idx) in pipe.lines"
                        :key="`hit-${idx}`"
                        :points="points"
                        fill="none"
                        stroke="transparent"
                        stroke-width="10"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        vector-effect="non-scaling-stroke"
                        pointer-events="stroke"
                      />
                      <template v-if="hoveredPipeCode === pipe.code">
                        <polyline
                          v-for="(points, idx) in pipe.lines"
                          :key="`glow-${idx}`"
                          :points="points"
                          fill="none"
                          :stroke="pipeHighlightColor"
                          stroke-opacity="0.45"
                          stroke-width="9"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          vector-effect="non-scaling-stroke"
                          pointer-events="none"
                        />
                      </template>
                    </g>

                    <!-- Элементы легенды (краны, клапаны и т.д.), обнаруженные на SVG по
                         id символа = code элемента (см. DSchemeNodes::_detected_elements). У каждого экземпляра свой
                         номер (КШ-1, КШ-2 — по порядку чтения схемы) и свои условия: клик
                         добавляет его строку в «Оборудование», при наведении видна подпись. -->
                    <template v-for="el in visibleElements">
                      <g
                        v-for="inst in el.instances"
                        :key="`el-${el.id}-${inst.n}`"
                        style="cursor: pointer"
                        @mouseenter="setHoveredEquipment(equipmentKey(el.id, inst.n), true)"
                        @mouseleave="setHoveredEquipment(null)"
                        @mousedown="captureFunctionalityCaret"
                        @click="onSvgElementClick(el, inst)"
                      >
                        <!-- подложка-«маркер» под рамкой (multiply: линии чертежа остаются чёрными) -->
                        <rect
                          v-if="equipmentWash(equipmentKey(el.id, inst.n))"
                          :x="inst.x - 1"
                          :y="inst.y - 1"
                          :width="inst.width + 2"
                          :height="inst.height + 2"
                          :fill="equipmentWash(equipmentKey(el.id, inst.n))"
                          style="mix-blend-mode: multiply"
                          pointer-events="none"
                        />
                        <rect
                          :x="inst.x - 1"
                          :y="inst.y - 1"
                          :width="inst.width + 2"
                          :height="inst.height + 2"
                          fill="transparent"
                        />
                        <rect
                          :x="inst.x - 1"
                          :y="inst.y - 1"
                          :width="inst.width + 2"
                          :height="inst.height + 2"
                          fill="none"
                          :stroke="equipmentStrokeColor(equipmentKey(el.id, inst.n))"
                          :stroke-width="
                            isEquipmentHighlighted(equipmentKey(el.id, inst.n))
                              ? svgViewBox.w / 120
                              : svgViewBox.w / 250
                          "
                          :stroke-dasharray="
                            isEquipmentHighlighted(equipmentKey(el.id, inst.n)) ||
                            isInEquipment(equipmentKey(el.id, inst.n))
                              ? null
                              : '1.5 1.5'
                          "
                        />
                      </g>
                    </template>

                    <!-- Подпись экземпляра под курсором (КШ-2): размер задан в пикселях экрана,
                         поэтому читается на любой схеме -->
                    <g v-if="equipmentLabel" pointer-events="none">
                      <rect
                        :x="equipmentLabel.x"
                        :y="equipmentLabel.y"
                        :width="equipmentLabel.w"
                        :height="equipmentLabel.h"
                        :rx="equipmentLabel.r"
                        :fill="equipmentHoverColor"
                      />
                      <text
                        :x="equipmentLabel.cx"
                        :y="equipmentLabel.cy"
                        text-anchor="middle"
                        dominant-baseline="central"
                        fill="#fff"
                        font-weight="600"
                        :font-size="equipmentLabel.fontSize"
                      >{{ equipmentLabel.text }}</text>
                      <!-- подсказка (data-name из SVG) — второй строкой под подписью -->
                      <text
                        v-if="equipmentLabel.hint"
                        :x="equipmentLabel.cx"
                        :y="equipmentLabel.hintCy"
                        text-anchor="middle"
                        dominant-baseline="central"
                        fill="#fff"
                        fill-opacity="0.85"
                        :font-size="equipmentLabel.hintFontSize"
                      >{{ equipmentLabel.hint }}</text>
                    </g>
                  </svg>
                </div>
              </div>

              <div class="mb-3" style="flex: 1 1 380px; min-width: 0">
                <div class="node-section-title mb-2">SVG</div>
                <!-- «Пересчитать» — в той же строке, в правом углу (ml-auto держит его справа и
                     когда строка переносится) -->
                <div class="d-flex align-center flex-wrap" style="gap: 8px">
                  <v-btn
                    small
                    outlined
                    :disabled="loading"
                    :loading="loading"
                    @click="triggerFileInput(entry)"
                  >
                    <v-icon left small>mdi-image-plus</v-icon>
                    {{ entry.id_file ? "Заменить SVG" : "Загрузить SVG" }}
                  </v-btn>
                  <v-btn
                    v-if="entry.id_file"
                    small
                    outlined
                    class="ml-auto"
                    :disabled="loading"
                    :loading="loading"
                    @click="detectAnchors"
                  >
                    <v-icon left small>mdi-refresh</v-icon>
                    Пересчитать
                  </v-btn>
                </div>

                <!-- варианты сборки: подпапки «2», «3» в Elements; меняют SVG и список оборудования -->
                <div v-if="hasVariants" class="mt-3">
                  <div class="node-section-title mb-2">Вариант сборки</div>
                  <v-btn-toggle
                    :value="selectedVariant"
                    mandatory
                    dense
                    color="primary"
                    @change="selectVariant"
                  >
                    <v-btn
                      v-for="variant in detectedVariants"
                      :key="`variant-${variant}`"
                      :value="variant"
                      small
                      text
                    >
                      {{ variant }}
                      <span v-if="variantRowCount(variant)" class="ml-1 text-caption">
                        ({{ variantRowCount(variant) }})
                      </span>
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <v-tabs v-model="svgInfoTab" dense grow class="mt-4 scheme-node-svg-tabs">
                  <v-tab>Коннекторы</v-tab>
                  <v-tab><span>Функциональность<br />элементов</span></v-tab>
                </v-tabs>

                <div v-if="svgInfoTab === 1" class="mt-3 scheme-node-functionalities">
                  <div class="d-flex align-center mb-2">
                    <span class="text-caption grey--text text--darken-1">
                      Наведите на плашку, чтобы подсветить связанные элементы на чертеже.
                    </span>
                    <v-spacer />
                    <v-btn
                      small
                      outlined
                      :disabled="functionalitiesLoading || !!functionalityDraft"
                      @click="startFunctionalityDraft"
                    >
                      <v-icon left small>mdi-plus</v-icon>Добавить
                    </v-btn>
                  </div>

                  <div v-if="functionalitiesLoading" class="py-4 text-center">
                    <v-progress-circular indeterminate size="20" width="2" />
                  </div>
                  <v-alert v-else-if="!functionalities.length && !functionalityDraft" dense text class="mb-2">
                    Функциональности пока не добавлены.
                  </v-alert>

                  <div
                    v-for="item in functionalities"
                    :key="`functionality-${item.id}`"
                    class="scheme-node-functionality"
                    @mouseenter="setHoveredFunctionality(item)"
                    @mouseleave="setHoveredFunctionality(null)"
                  >
                    <div class="d-flex align-start">
                      <div class="scheme-node-functionality__text">
                        <template v-for="(part, index) in functionalityParts(item)">
                          <a
                            v-if="part.type === 'element'"
                            :key="`part-${index}`"
                            :href="elementEditHref(part.id)"
                            target="_blank"
                            rel="noopener"
                            class="scheme-node-functionality__link"
                          >{{ part.text }}</a>
                          <span v-else :key="`part-${index}`">{{ part.text }}</span>
                        </template>
                      </div>
                      <v-btn icon x-small class="ml-2" title="Изменить" @click="editFunctionality(item)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon x-small title="Удалить" @click="removeFunctionality(item)">
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                    <div v-if="functionalityElements(item).length" class="d-flex flex-wrap mt-1" style="gap: 4px">
                      <v-chip v-for="element in functionalityElements(item)" :key="`${element.id}-${element.instance}`" x-small label>
                        {{ element.label }}
                      </v-chip>
                    </div>
                  </div>

                  <v-card v-if="functionalityDraft" outlined class="pa-3 mt-2">
                    <VueEditor
                      ref="functionalityEditor"
                      class="scheme-node-functionality__editor"
                      v-model="functionalityDraft.text"
                      :editorToolbar="functionalityToolbar"
                      placeholder="Текст функциональности"
                      @selection-change="rememberFunctionalitySelection"
                      @text-change="syncFunctionalityLinks"
                    />
                    <div class="text-caption grey--text text--darken-1 mt-2">
                      Выделите слова и кликните по элементу на SVG — выделенный текст станет ссылкой на его ID.
                    </div>
                    <div v-if="functionalityElements(functionalityDraft).length" class="d-flex flex-wrap mt-2" style="gap: 4px">
                      <v-chip
                        v-for="element in functionalityElements(functionalityDraft)"
                        :key="`${element.id}-${element.instance}`"
                        x-small
                        label
                        close
                        @click:close="removeFunctionalityElement(element)"
                      >
                        {{ element.label }}
                      </v-chip>
                    </div>
                    <div class="d-flex justify-end mt-3 scheme-node-functionality__actions" style="gap: 8px">
                      <v-btn small text @click="cancelFunctionalityDraft">Отмена</v-btn>
                      <v-btn
                        small
                        color="primary"
                        :disabled="!functionalityDraft.text.trim() || !functionalityDraft.element_refs.length || functionalitySaving"
                        :loading="functionalitySaving"
                        @click="persistFunctionality"
                      >
                        Сохранить
                      </v-btn>
                    </div>
                  </v-card>
                </div>

                <div v-else class="mt-4">
                  <!-- таблица «трубы × стороны» — результат разбора SVG: строки — трубы
                       справочника, найденные на картинке (по цвету линий), столбцы —
                       стороны узла; ячейка закрашена цветом трубы, если с этой стороны к
                       узлу подходит эта труба (касается края). Заголовок блока —
                       в левом верхнем углу таблицы -->
                  <v-simple-table dense class="anchor-pipes-table">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th style="padding: 0 4px 0 0">
                            <div class="node-section-title d-flex align-center" style="white-space: nowrap">
                              Коннекторы
                              <!-- справка вместо длинного описания: место нужно под «Оборудование» -->
                              <v-tooltip bottom max-width="340" content-class="node-dialog-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                                    mdi-information-outline
                                  </v-icon>
                                </template>
                                <span>
                                  Коннекторы определяются автоматически по SVG при загрузке
                                  и замене файла: труба из справочника, касающаяся края
                                  картинки, даёт коннектор на этой стороне. В таблице —
                                  трубы, найденные на SVG (строки), и стороны узла
                                  (столбцы): цветом закрашено, какая труба с какой стороны
                                  выходит на край. Если нужно пересчитать без замены файла —
                                  кнопка «Пересчитать». Элементы легенды на SVG (пунктирная
                                  рамка) — клик открывает их условия в блоке «Оборудование»
                                  ниже.
                                </span>
                              </v-tooltip>
                            </div>
                          </th>
                          <th
                            v-for="side in anchorSides"
                            :key="side.key"
                            class="text-center anchor-pipes-table__side"
                            style="padding: 0 6px"
                          >
                            <v-tooltip bottom content-class="node-dialog-tooltip">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon small v-bind="attrs" v-on="on">
                                  {{ side.icon }}
                                </v-icon>
                              </template>
                              <span>{{ side.label }}</span>
                            </v-tooltip>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="detectedPipesLoading || !anchorPipeRows.length">
                          <td
                            colspan="5"
                            class="grey--text text-caption"
                            style="padding: 0; height: 40px"
                          >
                            <template v-if="detectedPipesLoading">
                              <v-progress-circular
                                indeterminate
                                size="14"
                                width="2"
                                class="mr-2"
                              />
                              Разбираем SVG…
                            </template>
                            <template v-else-if="entry._file && entry._file.url">
                              На SVG узла не найдено труб из справочника.
                            </template>
                            <template v-else>
                              Загрузите SVG — трубы и коннекторы определятся автоматически.
                            </template>
                          </td>
                        </tr>
                        <template v-if="!detectedPipesLoading">
                          <tr
                            v-for="row in anchorPipeRows"
                            :key="row.key"
                            :class="{ 'eq-row--hovered': isPipeRowHovered(row) }"
                            @mouseenter="setHoveredPipe(row.pipe ? row.key : null)"
                            @mouseleave="setHoveredPipe(null)"
                          >
                            <td
                              :title="row.title"
                              style="max-width: 0; width: 100%; padding: 0 4px 0 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
                            >
                              <span
                                class="anchor-pipes-table__swatch"
                                :style="{ backgroundColor: row.color }"
                              />
                              <span
                                class="font-weight-medium"
                                :class="{ 'blue--text text--darken-2': isPipeRowHovered(row) }"
                              >{{ row.label }}</span>
                              <span
                                v-if="row.pipe"
                                :class="
                                  isPipeRowHovered(row)
                                    ? 'blue--text text--darken-2'
                                    : 'grey--text text--darken-1'
                                "
                              >
                                {{ row.pipe.name_ru }}
                              </span>
                            </td>
                            <td
                              v-for="side in anchorSides"
                              :key="side.key"
                              class="anchor-pipes-table__side"
                              style="padding: 0 6px"
                            >
                              <div
                                v-if="row.counts[side.key]"
                                class="anchor-pipes-table__fill"
                                :style="{
                                  backgroundColor: row.color,
                                  color: row.textColor,
                                }"
                                :title="anchorCellTitle(row, side)"
                              >
                                <template v-if="row.counts[side.key] > 1">
                                  {{ row.counts[side.key] }}
                                </template>
                              </div>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <v-alert
                  v-if="entry._file && entry._file.url && svgViewBoxError"
                  type="warning"
                  dense
                  text
                  class="mt-4 mb-0"
                >
                  Не удалось определить размеры SVG — коннекторы сейчас недоступны
                </v-alert>

                <!-- Оборудование узла: найденные на SVG элементы легенды и условия подбора
                     номенклатуры — в правой колонке рядом с картинкой -->
                <div class="mt-4">
                  <div class="node-section-title mb-2 d-flex align-center">
                    Оборудование
                    <span v-if="equipmentRows.length" class="ml-1">
                      ({{ equipmentRows.length }})
                    </span>
                    <!-- справка вместо подписи под заголовком -->
                    <v-tooltip bottom max-width="340" content-class="node-dialog-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>
                        Клик по элементу на схеме добавляет его в таблицу: у каждого экземпляра
                        (КШ-1, КШ-2…) свои условия, подпись видна при наведении. Лупа справа открывает
                        номенклатуру, подходящую под условия этого экземпляра. Клик по строке
                        раскрывает его условия. Если на чертеже несколько вариантов сборки
                        (подпапки «2», «3» в группе Elements), переключатель над таблицей меняет
                        картинку и список: номера КШ-1, КШ-2… сквозные по всему узлу, не повторяются
                        между вариантами.
                      </span>
                    </v-tooltip>
                  </div>
            <div v-if="!detectedElements.length" class="grey--text text-caption mb-3">
              На SVG узла не найдено элементов легенды.
            </div>
            <div v-else-if="!equipmentRows.length" class="grey--text text-caption mb-3">
              Кликните по элементу на схеме (при наведении видна подпись, например КШ-1) — он
              появится здесь, и для него можно задать условия подбора.
            </div>

            <v-simple-table v-else dense class="mb-3 equipment-table">
              <template v-slot:default>
                <tbody>
                  <template v-for="row in equipmentRows">
                    <tr
                      :id="`eq-row-${row.key}`"
                      :key="`eq-row-${row.key}`"
                      style="cursor: pointer"
                      :class="{
                        'eq-row--expanded': isEquipmentExpanded(row.key),
                        'eq-row--current': isEquipmentRowHovered(row.key),
                      }"
                      @click="toggleExpandedEquipment(row)"
                      @mouseenter="setHoveredEquipment(row.key, false)"
                      @mouseleave="setHoveredEquipment(null)"
                    >
                      <td style="width: 32px; padding: 10px 4px">
                        <v-img
                          :src="elementIconSrc(row.el)"
                          contain
                          width="24"
                          height="24"
                        />
                      </td>
                      <td style="padding: 10px 0 10px 8px">
                        <span
                          class="font-weight-medium"
                          :class="{ 'purple--text text--darken-2': isEquipmentRowHovered(row.key) }"
                        >{{ row.label }}</span>
                        <span
                          v-if="row.el.name_ru && row.label !== row.el.name_ru"
                          class="ml-1"
                          :class="
                            isEquipmentRowHovered(row.key)
                              ? 'purple--text text--darken-2'
                              : 'grey--text'
                          "
                        >{{ row.el.name_ru }}</span>
                        <span
                          v-if="row.inst && row.inst.hint"
                          class="ml-1 text-caption"
                          :class="
                            isEquipmentRowHovered(row.key)
                              ? 'purple--text text--darken-2'
                              : 'teal--text text--darken-1'
                          "
                        >({{ row.inst.hint }})</span>
                        <span v-if="row.missing" class="orange--text text--darken-3 text-caption ml-1">
                          нет на схеме
                        </span>
                      </td>
                      <td style="width: 24px; padding: 10px 0" class="text-right">
                        <v-icon small>
                          {{ isEquipmentExpanded(row.key) ? "mdi-chevron-up" : "mdi-chevron-down" }}
                        </v-icon>
                      </td>
                      <td style="width: 32px; padding: 10px 4px 10px 0" class="text-right" @click.stop>
                        <IconTooltip
                          icon-text="mdi-magnify"
                          size-icon="22"
                          :text-tooltip="
                            equipmentHasSavedRules(row)
                              ? 'Показать подходящую номенклатуру'
                              : 'Сначала задайте условие'
                          "
                          :is-disabled="!equipmentHasSavedRules(row)"
                          @click-icon="openEquipmentCandidates(row)"
                        />
                      </td>
                    </tr>
                    <tr
                      v-if="isEquipmentExpanded(row.key)"
                      :key="`eq-expand-${row.key}`"
                      class="eq-expand-row"
                      :class="{ 'eq-row--current': isEquipmentRowHovered(row.key) }"
                    >
                      <td colspan="4" class="pb-4 pt-2" style="padding-left: 8px; padding-right: 8px">
                        <!-- условие: все поля в одну строку; характеристика и значение делят
                             оставшуюся ширину 3:2, операнд и корзина — фиксированные -->
                        <div
                          v-for="rule in rowRules(row)"
                          :key="rule.uid"
                          class="d-flex align-center mb-2"
                          style="gap: 6px; flex-wrap: nowrap"
                        >
                          <v-autocomplete
                            :value="rule.id_characteristic"
                            :items="characteristicOptionsFor(row.el, rule)"
                            item-text="label"
                            item-value="id"
                            label="Характеристика"
                            dense
                            hide-details
                            outlined
                            style="flex: 3 1 0; min-width: 0"
                            @change="
                              updateNomenclatureRule(rule.uid, 'id_characteristic', $event)
                            "
                          />
                          <v-select
                            :value="rule.operand"
                            :items="operandOptionsFor(rule.id_characteristic)"
                            item-text="label"
                            item-value="value"
                            label="Операнд"
                            dense
                            hide-details
                            outlined
                            style="flex: 0 0 72px; min-width: 0"
                            @change="updateNomenclatureRule(rule.uid, 'operand', $event)"
                          />
                          <v-select
                            v-if="isDictionaryCharacteristic(rule.id_characteristic)"
                            :value="rule.value"
                            :items="dictionaryValuesFor(rule.id_characteristic)"
                            :loading="dictionaryValuesLoadingFor(rule.id_characteristic)"
                            label="Значение"
                            dense
                            hide-details
                            outlined
                            style="flex: 2 1 0; min-width: 0"
                            @focus="loadDictionaryValuesFor(rule.id_characteristic)"
                            @change="updateNomenclatureRule(rule.uid, 'value', $event)"
                          />
                          <v-combobox
                            v-else
                            :value="rule.value"
                            :items="valuesFor(rule.id_characteristic)"
                            :loading="valuesLoadingFor(rule.id_characteristic)"
                            label="Число"
                            dense
                            hide-details
                            outlined
                            style="flex: 2 1 0; min-width: 0"
                            @focus="loadValuesFor(rule.id_characteristic)"
                            @change="updateNomenclatureRule(rule.uid, 'value', $event)"
                          />
                          <IconTooltip
                            icon-text="mdi-delete-outline"
                            text-tooltip="Удалить условие"
                            color-icon="red"
                            @click-icon="removeNomenclatureRule(rule.uid)"
                          />
                        </div>

                        <div
                          v-if="!hasMainCharacteristics(row.el)"
                          class="text-caption orange--text text--darken-3"
                        >
                          <template v-if="!row.el.id_family">
                            Привяжите к элементу «{{ row.el.name_ru || row.el.code }}» семейство
                            номенклатуры —
                            <a :href="elementEditHref(row.el.id)" target="_blank">Элементы легенды</a>.
                          </template>
                          <template v-else>
                            У семейства «{{ row.el.family_name }}» не выбраны основные параметры —
                            <a :href="familyEditHref(row.el.id_family)" target="_blank">Номенклатура</a>
                            (редактирование семейства → «Основные параметры разного типа оборудования»).
                          </template>
                        </div>
                        <div class="d-flex align-center flex-wrap">
                          <v-btn
                            v-if="hasMainCharacteristics(row.el)"
                            small
                            text
                            @click="addNomenclatureRule(row)"
                          >
                            <v-icon left small>mdi-plus-thick</v-icon>
                            Добавить условие
                          </v-btn>
                          <v-spacer />
                          <!-- убрать строку можно, пока в ней нет условий: с условиями сначала
                               удаляем их — так данные не пропадают случайно -->
                          <v-btn
                            v-if="!rowRules(row).length"
                            small
                            text
                            class="grey--text text--darken-1"
                            @click="removeEquipmentRow(row)"
                          >
                            Убрать из оборудования
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-simple-table>

              
                </div>
              </div>
            </div>

              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-alert v-if="saveErrorMessage" type="error" dense text class="mt-4">
            {{ saveErrorMessage }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="isEditing && !entry.is_folder"
            text
            small
            @click="openInSchemeNodeRules"
          >
            <v-icon left small>mdi-vector-line</v-icon>
            Перейти в правила
          </v-btn>
          <v-spacer />
          <v-btn text @click="dialog = false">Закрыть</v-btn>
          <v-btn
            color="primary"
            text
            :disabled="!entry.code || loading"
            :loading="loading"
            @click="saveEntry"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Подходящая номенклатура конкретного экземпляра оборудования (поверх диалога узла) -->
    <v-dialog v-model="candidatesDialog" max-width="760" scrollable>
      <v-card v-if="candidatesRow">
        <v-card-title class="text-subtitle-1 font-weight-medium">
          <v-img
            :src="elementIconSrc(candidatesRow.el)"
            contain
            max-width="28"
            height="28"
            class="mr-3 flex-grow-0"
          />
          Подходящая номенклатура — {{ candidatesRow.label }}
          <span v-if="candidatesRow.el.name_ru && candidatesRow.label !== candidatesRow.el.name_ru"
                class="grey--text ml-2 font-weight-regular">{{ candidatesRow.el.name_ru }}</span>
        </v-card-title>
        <v-card-text>
          <div class="text-caption grey--text mb-1">Условия</div>
          <div class="mb-4">
            <v-chip
              v-for="rule in candidatesRowRules"
              :key="rule.uid"
              small
              outlined
              class="mr-1 mb-1"
            >
              {{ candidateRuleText(rule) }}
            </v-chip>
          </div>

          <div v-if="nomenclatureCandidatesLoading" class="text-center py-6">
            <v-progress-circular indeterminate size="28" />
          </div>
          <div v-else-if="nomenclatureCandidatesLoaded">
            <div v-if="!nomenclatureCandidates.length" class="grey--text text-caption">
              {{ nomenclatureCandidatesMessage || "Подходящей номенклатуры не найдено" }}
            </div>
            <v-simple-table v-else dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left" style="width: 40px">№</th>
                    <th class="text-left">Номенклатура</th>
                    <th
                      v-for="char in nomenclatureUsedCharacteristics"
                      :key="char.id"
                      class="text-left"
                    >
                      {{ char.name }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in nomenclatureCandidates" :key="item.id">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <div v-if="item.group" class="grey--text text-caption">{{ item.group }}</div>
                      <div>{{ item.name }}</div>
                    </td>
                    <td v-for="char in nomenclatureUsedCharacteristics" :key="char.id">
                      {{ formatCandidateValue(item, char) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="candidatesDialog = false">Закрыть</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Диалог удаления -->
    <v-dialog
      :value="deleteModal"
      max-width="500"
      @input="deleteModalCommit"
    >
      <v-card>
        <v-card-title>
          Вы точно хотите удалить узел "{{ entry.code }}"?
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteModalCommit(false)">Закрыть</v-btn>
          <v-btn
            color="red"
            text
            :loading="loading"
            @click="confirmDelete"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import IconTooltip from "@/components/common/IconTooltip.vue";
import { SchemeNode } from "@/helpers/constructors";
import { functionalityTextParts, linkedElementIds, linkedElementRefs } from "@/helpers/schemeNodeFunctionalities";
import { VueEditor } from "vue2-editor";
import {
  OPERANDS,
  DICTIONARY_TYPE_CODE,
} from "@/store/modules/scheme-node-nomenclature-rules";

// Увеличенное превью узла при наведении на его миниатюру в дереве: размер рамки и отступ от курсора, px
const HOVER_PREVIEW_WIDTH = 300;
const HOVER_PREVIEW_HEIGHT = 380;
const HOVER_PREVIEW_GAP = 24;

// Превью SVG узла: ширина — доля ширины панели (справа остаётся колонка с кнопками, таблицей
// труб и оборудованием) в пределах ANCHOR_SVG_MIN_WIDTH..ANCHOR_SVG_MAX_WIDTH; высота подбирается
// по пропорциям картинки — не меньше 360px и не больше ANCHOR_SVG_MAX_HEIGHT. Рамки
// элементов, подложка и сама картинка заданы в единицах viewBox, а клики ловятся на самих
// SVG-элементах — масштаб картинки на них не влияет.
const ANCHOR_SVG_MIN_WIDTH = 380;
const ANCHOR_SVG_MAX_WIDTH = 760;
const ANCHOR_SVG_WIDTH_SHARE = 0.5;
// Минимальная ширина правой колонки (кнопки, таблица труб, оборудование) и зазор между колонками —
// превью не должно занимать больше, чем остаётся за вычетом них, иначе колонка уедет вниз
const ANCHOR_RIGHT_COL_MIN = 380;
const ANCHOR_COLS_GAP = 24;
const ANCHOR_SVG_MIN_HEIGHT = 360;
// Высота превью — фиксированный потолок; только на невысоких экранах он ужимается, чтобы схема
// целиком помещалась в диалог (диалог занимает до 90% окна, шапка, подвал и заголовки над
// картинкой забирают ~330px)
const ANCHOR_SVG_MAX_HEIGHT = 820;
const ANCHOR_VIEWPORT_SHARE = 0.9;
const ANCHOR_VIEWPORT_RESERVE_PX = 330;
// Цвет труб без заданного цвета и строки «Не определена» (точки без трубы) в таблице «трубы × стороны»
const ANCHOR_PIPE_FALLBACK_COLOR = "#9e9e9e";
// Расстояние (в единицах viewBox), в пределах которого сохранённая точка без кода трубы считается
// той же точкой, что в свежем разборе SVG (это радиус слияния соседних точек на бэке)
const ANCHOR_PIPE_MATCH_DISTANCE = 1;
// Подсветка трубы на превью при наведении — тот же синий, что у рамки элемента (equipmentStrokeColor)
const PIPE_HIGHLIGHT_COLOR = "#1976D2";
// Размер текста подписи экземпляра (КШ-2) на превью, px экрана
const EQUIPMENT_LABEL_FONT_PX = 12;
// Экземпляр оборудования под курсором («текущий»): рамка на SVG, подпись над ним и подсветка его строки
const EQUIPMENT_HOVER_COLOR = "#7B1FA2";
// Раскрытая строка — постоянная красная рамка, пока открыты условия
const EQUIPMENT_EXPANDED_COLOR = "#D32F2F";
// Остальные экземпляры того же типа, пока курсор на одном из них: тёмное золото — жёлтое семейство,
// но с контрастом к белому фону 3.9:1 (у чистого жёлтого и янтарного меньше 2:1) и далеко от фиолетового
const EQUIPMENT_SIBLING_COLOR = "#A87900";
// Заливка-подложка под рамкой (режим multiply): под курсором — фиолетовая, остальные экземпляры того же
// типа — жёлтая «маркерная»
const EQUIPMENT_HOVER_WASH = "rgba(123,31,162,0.14)";
const EQUIPMENT_SIBLING_WASH = "rgba(255,214,0,0.55)";

// Цвет цифры на плашке цвета трубы: тёмный на светлых трубах (газ, ХВС), белый на тёмных
function pipeFillTextColor(color) {
  const hex = /^#?([0-9a-f]{6})$/i.exec(color || "");
  if (!hex) return "#fff";
  const n = parseInt(hex[1], 16);
  const luma = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
  return luma > 160 ? "rgba(0, 0, 0, 0.75)" : "#fff";
}

export default {
  name: "SchemeNodesTree",
  components: { IconTooltip, VueEditor },
  data: () => ({
    dialog: false,
    isEditing: false,
    usage: null,
    // увеличенное превью узла под курсором: адрес картинки и положение (url пуст — не показываем)
    hoverPreview: { url: "", x: 0, y: 0 },
    // список схем «Используется в схемах»: при каждом открытии узла свёрнут, разворачивается по клику
    usageOpen: false,
    // вкладки правой панели SVG: функциональности по умолчанию, коннекторы остаются отдельной вкладкой
    svgInfoTab: 0,
    functionalities: [],
    functionalitiesLoading: false,
    functionalitySaving: false,
    functionalityDraft: null,
    functionalitySelection: null,
    functionalityToolbar: [["bold", "italic", "underline"]],
    hoveredFunctionalityRefs: [],
    parentCode: "",
    saveErrorMessage: "",
    uploadTargetNode: null,
    requiredFieldRules: [(v) => !!v || "Обязательное поле"],
    anchorSides: [
      { key: "top", label: "Сверху", icon: "mdi-arrow-up" },
      { key: "left", label: "Слева", icon: "mdi-arrow-left" },
      { key: "bottom", label: "Снизу", icon: "mdi-arrow-down" },
      { key: "right", label: "Справа", icon: "mdi-arrow-right" },
    ],
    notePanelIndex: [],
    seoPanelIndex: [],
    expandedPanels: [0],
    svgViewBox: null,
    svgViewBoxError: false,
    svgViewBoxLoading: false,
    // Последняя высота превью — лоадер до загрузки viewBox занимает столько же места,
    // чтобы блоки ниже не прыгали (пока viewBox неизвестен, свою высоту не посчитать).
    anchorSvgHeightHint: ANCHOR_SVG_MIN_HEIGHT,
    // Ширина панели с превью (px) — от неё зависит ширина превью; измеряется при показе
    // картинки и при изменении размера окна (0 — ещё не измерена, берём минимум)
    panelWidth: 0,
    // Высота окна браузера (px) — на невысоких экранах ужимает потолок высоты превью
    windowHeight: 0,
    // Трубы справочника, найденные в SVG узла (строки таблицы «трубы × стороны»), и свежий
    // разбор точек крепления с кодом трубы — по нему опознаём трубу у точек, сохранённых
    // без неё (см. getDetectedPipes)
    detectedPipes: [],
    detectedAnchorPipes: null,
    detectedPipesLoading: false,
    // Труба под курсором (код) — подсвечивается на SVG и в таблице «Коннекторы», как оборудование;
    // fromSvg — наведение пришло с самой картинки (а не со строки таблицы): тогда подсвечиваем и строку
    hoveredPipeCode: null,
    hoveredPipeFromSvg: false,
    // Элементы легенды (краны, клапаны и т.д.), обнаруженные на SVG узла —
    // подсветка/клик для привязки номенклатуры к конкретному элементу.
    detectedElements: [],
    // Варианты сборки узла ([1] или [1, 2, 3] — подпапки «2», «3» в Elements) и выбранный сейчас: от него зависят
    // картинка узла, рамки элементов и строки «Оборудования»
    detectedVariants: [1],
    selectedVariant: 1,
    // Экземпляр оборудования под курсором (ключ «id_элемента-номер», см. equipmentKey) и признак
    // «наведение пришло с самого SVG» — тогда подсвечиваем и его строку в таблице
    hoveredEquipmentKey: null,
    hoveredEquipmentFromSvg: false,
    // Экземпляры, добавленные в «Оборудование» кликом по SVG или оставшиеся без условий (строки с
    // условиями есть в таблице и так)
    addedEquipmentKeys: [],
    // ключи строк «Оборудования», раскрытых сейчас — можно держать открытыми несколько сразу
    expandedEquipmentKeys: [],
    // Модалка «Подходящая номенклатура» экземпляра оборудования: ключ строки (equipmentKey) и
    // характеристики его условий — они же колонки таблицы
    candidatesDialog: false,
    candidatesRowKey: null,
    candidateCharacteristicIds: [],
  }),
  computed: {
    // Ширина превью: доля ширины панели в пределах MIN..MAX
    anchorSvgWidth() {
      const byPanel = Math.min(
        Math.round(this.panelWidth * ANCHOR_SVG_WIDTH_SHARE),
        this.panelWidth - ANCHOR_COLS_GAP - ANCHOR_RIGHT_COL_MIN
      );
      return Math.min(Math.max(byPanel, ANCHOR_SVG_MIN_WIDTH), ANCHOR_SVG_MAX_WIDTH);
    },
    // Высота превью SVG: такая, при которой картинка заполняет всю ширину (масштаб W / w,
    // высота масштаб·h), но в пределах MIN..MAX — для высоких узких схем упирается в MAX
    anchorSvgHeight() {
      const box = this.svgViewBox;
      if (!box || !box.w || !box.h) return ANCHOR_SVG_MIN_HEIGHT;
      const full = Math.round((this.anchorSvgWidth / box.w) * box.h);
      return Math.min(Math.max(full, ANCHOR_SVG_MIN_HEIGHT), this.anchorSvgMaxHeight);
    },
    // Потолок высоты превью: фиксированный ANCHOR_SVG_MAX_HEIGHT, на невысоком экране — меньше
    anchorSvgMaxHeight() {
      const byScreen = Math.round(
        (this.windowHeight || 900) * ANCHOR_VIEWPORT_SHARE - ANCHOR_VIEWPORT_RESERVE_PX
      );
      return Math.max(Math.min(ANCHOR_SVG_MAX_HEIGHT, byScreen), ANCHOR_SVG_MIN_HEIGHT);
    },
    // viewBox превью — viewBox самой картинки
    anchorViewBox() {
      const box = this.svgViewBox;
      return box ? `${box.minX} ${box.minY} ${box.w} ${box.h}` : "";
    },
    // Трубы для наложения на превью: линии каждой трубы строками points="x,y x,y …"
    // (геометрия приходит с бэка в _detected_pipes[].segments, в координатах viewBox)
    pipeOverlays() {
      return this.detectedPipes
        .filter((pipe) => pipe.segments && pipe.segments.length)
        .map((pipe) => ({
          code: pipe.code,
          lines: pipe.segments.map((segment) => segment.map(([x, y]) => `${x},${y}`).join(" ")),
        }));
    },
    equipmentHoverColor() {
      return EQUIPMENT_HOVER_COLOR;
    },
    pipeHighlightColor() {
      return PIPE_HIGHLIGHT_COLOR;
    },
    // Строки таблицы «трубы × стороны»: трубы справочника, найденные на SVG (в порядке
    // справочника), и сколько сохранённых точек крепления у каждой на каждой стороне. Точки,
    // которым труба не нашлась (например, остались от прежней версии SVG), собираются в
    // строку «Не определена»
    anchorPipeRows() {
      const newCounts = () => ({ top: 0, left: 0, bottom: 0, right: 0 });
      const rows = this.detectedPipes.map((pipe) => {
        const color = pipe.color || ANCHOR_PIPE_FALLBACK_COLOR;
        return {
          key: pipe.code,
          label: pipe.marking_ru || pipe.name_ru || pipe.code,
          title: [pipe.marking_ru, pipe.name_ru].filter(Boolean).join(" — "),
          pipe,
          color,
          textColor: pipeFillTextColor(color),
          counts: newCounts(),
        };
      });
      const byCode = {};
      rows.forEach((row) => {
        byCode[row.key] = row;
      });

      let unknown = null;
      const anchors = (this.entry && this.entry.anchors) || {};
      this.anchorSides.forEach(({ key: side }) => {
        (anchors[side] || []).forEach((point) => {
          const row = byCode[point.pipe || this.detectedPipeCodeAt(side, point)];
          if (row) {
            row.counts[side] += 1;
            return;
          }
          unknown = unknown || {
            key: "__unknown",
            label: "Не определена",
            title:
              "Коннектор есть, а подходящей трубы на SVG нет — нажмите «Пересчитать»",
            pipe: null,
            color: ANCHOR_PIPE_FALLBACK_COLOR,
            textColor: pipeFillTextColor(ANCHOR_PIPE_FALLBACK_COLOR),
            counts: newCounts(),
          };
          unknown.counts[side] += 1;
        });
      });
      return unknown ? [...rows, unknown] : rows;
    },
    // max-height в vh — страховка для невысоких экранов: картинка не должна занимать
    // больше экрана (viewBox + letterbox вписывают её целиком при любой высоте)
    anchorSvgStyle() {
      return {
        width: `${this.anchorSvgWidth}px`,
        height: `${this.anchorSvgHeight}px`,
        display: "block",
        border: "1px solid #ddd",
        background: "#e0e0e0",
      };
    },
    anchorSvgPlaceholderStyle() {
      return {
        width: `${this.anchorSvgWidth}px`,
        height: `${this.anchorSvgHeightHint}px`,
        border: "1px solid #ddd",
        background: "#fafafa",
      };
    },
    ...mapState("SchemeNodesModule", ["tree", "loading", "entry", "deleteModal"]),
    ...mapState("SchemeNodeNomenclatureRulesModule", {
      nomenclatureRules: "rules",
      nomenclatureCharacteristicsRaw: "characteristics",
      nomenclatureCandidates: "candidates",
      nomenclatureCandidatesLoaded: "candidatesLoaded",
      nomenclatureCandidatesMessage: "candidatesMessage",
      nomenclatureCandidatesLoading: "candidatesLoading",
      valuesByCharacteristic: "valuesByCharacteristic",
      valuesLoadingByCharacteristic: "valuesLoadingByCharacteristic",
      dictionaryValuesById: "dictionaryValuesById",
      dictionaryValuesLoadingById: "dictionaryValuesLoadingById",
    }),
    ...mapGetters("SchemeNodeNomenclatureRulesModule", [
      "characteristicOptions",
      "characteristicById",
    ]),
    operandOptions() {
      return OPERANDS;
    },
    hasVariants() {
      return this.detectedVariants.length > 1;
    },
    // Картинка узла: у чертежа с вариантами сборки файл содержит все варианты разом, поэтому рисуем
    // чертёж, который отдаёт бэк для выбранного варианта (v — id файла, чтобы замена чертежа сбросила кеш)
    anchorImageUrl() {
      return this.variantImageUrl(this.selectedVariant);
    },
    // Элементы с экземплярами выбранного варианта (общие для всех вариантов, variant = null, видны всегда)
    visibleElements() {
      return this.detectedElements
        .map((el) => ({ ...el, instances: el.instances.filter((inst) => this.isInstanceInVariant(inst)) }))
        .filter((el) => el.instances.length);
    },
    // Строки таблицы «Оборудование»: экземпляры элементов на SVG выбранного варианта, у которых есть условия
    // или которые добавили кликом по схеме. Плюс условия экземпляров, которых на SVG уже нет (после
    // замены файла кранов стало меньше) — строка «нет на схеме», чтобы такие условия не терялись.
    // Условия экземпляров другого варианта здесь не показываются: они есть на SVG, но в другом варианте.
    equipmentRows() {
      const added = new Set(this.addedEquipmentKeys);
      const withRules = new Set(
        this.nomenclatureRules
          .filter((r) => r.id_scheme_node_element && r.element_instance)
          .map((r) => this.equipmentKey(r.id_scheme_node_element, r.element_instance))
      );
      const rows = [];
      const matched = new Set();

      this.visibleElements.forEach((el) => {
        el.instances.forEach((inst) => {
          const key = this.equipmentKey(el.id, inst.n);
          if (!added.has(key) && !withRules.has(key)) return;
          rows.push({ key, el, n: inst.n, inst, label: inst.label, missing: false });
          matched.add(key);
        });
      });

      // экземпляры, которые есть на SVG (в любом варианте)
      const drawn = new Set();
      this.detectedElements.forEach((el) => {
        el.instances.forEach((inst) => drawn.add(this.equipmentKey(el.id, inst.n)));
      });

      this.nomenclatureRules.forEach((rule) => {
        if (!rule.id_scheme_node_element || !rule.element_instance) return;
        const key = this.equipmentKey(rule.id_scheme_node_element, rule.element_instance);
        if (matched.has(key) || drawn.has(key)) return;
        matched.add(key);
        const el = this.detectedElements.find((e) => e.id === rule.id_scheme_node_element) || {
          id: rule.id_scheme_node_element,
          code: "",
          name_ru: rule.element_name,
          marking_ru: rule.element_marking,
          id_family: null,
          main_characteristic_ids: [],
        };
        const prefix = el.marking_ru || rule.element_marking || el.name_ru || "Элемент";
        rows.push({
          key,
          el,
          n: rule.element_instance,
          inst: null,
          label: `${prefix}-${rule.element_instance}`,
          missing: true,
        });
      });

      return rows;
    },
    equipmentKeys() {
      return new Set(this.equipmentRows.map((row) => row.key));
    },
    candidatesRow() {
      return this.equipmentRows.find((row) => row.key === this.candidatesRowKey) || null;
    },
    candidatesRowRules() {
      return this.candidatesRow ? this.rowRules(this.candidatesRow).filter((rule) => rule.id) : [];
    },
    // Пикселей экрана в одной единице viewBox картинки — чтобы подпись была одного размера на любой схеме
    previewScale() {
      const box = this.svgViewBox;
      if (!box || !box.w || !box.h) return 1;
      return Math.min(this.anchorSvgWidth / box.w, this.anchorSvgHeight / box.h);
    },
    // Экземпляр по ключу — для подписи и подсветки
    instanceByKey() {
      const map = {};
      this.detectedElements.forEach((el) => {
        el.instances.forEach((inst) => {
          map[this.equipmentKey(el.id, inst.n)] = { el, inst };
        });
      });
      return map;
    },
    // Подпись под курсором: плашка с текстом «КШ-2» над экземпляром (у верхнего края — под ним),
    // в координатах viewBox; размеры считаются из пикселей, текст не масштабируется вместе со схемой
    equipmentLabel() {
      const box = this.svgViewBox;
      const found = this.hoveredEquipmentKey && this.instanceByKey[this.hoveredEquipmentKey];
      if (!box || !found) return null;

      const unit = 1 / this.previewScale;
      const fontSize = EQUIPMENT_LABEL_FONT_PX * unit;
      const text = found.inst.label;
      const hint = found.inst.hint || "";
      const hintFontSize = fontSize * 0.85;
      const w =
        Math.max(text.length * fontSize, hint.length * hintFontSize) * 0.62 + 12 * unit;
      const h = fontSize + 6 * unit + (hint ? hintFontSize + 2 * unit : 0);
      const gap = 4 * unit;
      const x = Math.min(
        Math.max(found.inst.x + found.inst.width / 2 - w / 2, box.minX),
        box.minX + box.w - w
      );
      let y = found.inst.y - h - gap;
      if (y < box.minY) y = found.inst.y + found.inst.height + gap;

      // подпись — в первой строке плашки, подсказка (если есть) — во второй
      const cy = y + 3 * unit + fontSize / 2;
      const hintCy = cy + fontSize / 2 + 2 * unit + hintFontSize / 2;

      return { x, y, w, h, r: 3 * unit, fontSize, cx: x + w / 2, cy, text, hint, hintFontSize, hintCy };
    },
    // Колонки таблицы подбора — характеристики условий, по которым шёл подбор (см.
    // showNomenclatureCandidates; без повторов — у двух условий может быть одна и та же).
    nomenclatureUsedCharacteristics() {
      const ids = [];
      this.candidateCharacteristicIds.forEach((id) => {
        if (id !== null && id !== undefined && !ids.includes(id)) {
          ids.push(id);
        }
      });
      return ids.map((id) => {
        const found = this.nomenclatureCharacteristicsRaw.find(
          (c) => c.id === id
        );
        return {
          id,
          name: found ? found.name : `#${id}`,
          postfix: found ? found.postfix : null,
        };
      });
    },
  },
  watch: {
    // Запоминаем высоту превью, когда viewBox известен (для лоадера следующего узла)
    svgViewBox(box) {
      if (box) this.anchorSvgHeightHint = this.anchorSvgHeight;
      this.$nextTick(this.measurePanelWidth);
    },
    // Закрытие диалога любым способом (кнопка, esc, клик вне) идёт через
    // v-model, а не через отдельный метод — проще всего ловить здесь.
    dialog(value) {
      if (!value) this.syncOpenQuery(null);
    },
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.measurePanelWidth);
    window.removeEventListener("scroll", this.hidePreview, true);
  },
  async mounted() {
    window.addEventListener("resize", this.measurePanelWidth);
    window.addEventListener("scroll", this.hidePreview, true);
    this.windowHeight = window.innerHeight;
    await this.getTree();
    this.fetchCharacteristics();
    this.openFromQuery();
  },
  methods: {
    // Увеличенное превью только под курсором, стоящим на самой миниатюре узла (не на строке): делегирование
    // на корне дерева, адрес картинки — в data-node-preview на v-img (у папок и остальной строки его нет)
    onTreeMouseMove(event) {
      const holder = event.target.closest("[data-node-preview]");
      const url = holder ? holder.getAttribute("data-node-preview") : "";
      if (!url) {
        this.hidePreview();
        return;
      }

      // справа от курсора; у правого края окна — слева; по вертикали центрируем на курсоре в пределах окна
      let x = event.clientX + HOVER_PREVIEW_GAP;
      if (x + HOVER_PREVIEW_WIDTH > window.innerWidth - 8) {
        x = event.clientX - HOVER_PREVIEW_GAP - HOVER_PREVIEW_WIDTH;
      }
      const y = Math.min(
        Math.max(8, event.clientY - HOVER_PREVIEW_HEIGHT / 2),
        window.innerHeight - HOVER_PREVIEW_HEIGHT - 8
      );
      this.hoverPreview = { url, x, y };
    },
    hidePreview() {
      if (this.hoverPreview.url) this.hoverPreview = { url: "", x: 0, y: 0 };
    },
    // ссылка на окно схемы в разделе «Схемы» (в новой вкладке, чтобы не потерять карточку узла)
    schemeLink(scheme) {
      return this.$router.resolve({
        path: "/module-schemes",
        query: { open: String(scheme.id), type: scheme.type },
      }).href;
    },
    toggleUsage() {
      this.usageOpen = !this.usageOpen;
    },
    ...mapActions("SchemeNodesModule", [
      "getTree",
      "createEntry",
      "updateEntry",
      "deleteEntry",
      "uploadSvgForNode",
      "getSvgViewbox",
      "getDetectedElements",
      "getDetectedPipes",
      "detectAnchorsForNode",
      "getUsage",
      "getFunctionalities",
      "saveFunctionality",
      "deleteFunctionality",
    ]),
    ...mapMutations("SchemeNodesModule", [
      "setEntry",
      "clearEntry",
      "deleteModalCommit",
    ]),
    ...mapActions("SchemeNodeNomenclatureRulesModule", [
      "fetchCharacteristics",
      "fetchRules",
      "clearRules",
      "addDraftRule",
      "updateRule",
      "removeRule",
      "fetchCandidates",
      "fetchCharacteristicValues",
      "fetchDictionaryValues",
    ]),
    // Ссылка на элемент в справочнике легенды сразу с открытой модалкой этого элемента
    elementEditHref(idElement) {
      return this.$router.resolve({
        path: "/scheme-node-elements",
        query: { element: idElement },
      }).href;
    },
    // Ссылка на семейство в «Номенклатуре» сразу с открытым окном редактирования
    familyEditHref(idFamily) {
      return this.$router.resolve({
        path: "/nomenclaturesTree",
        query: { family: idFamily, action: "edit" },
      }).href;
    },
    // У элемента задано семейство, и в нём выбраны основные параметры
    hasMainCharacteristics(el) {
      return !!el.id_family && (el.main_characteristic_ids || []).length > 0;
    },
    // Характеристики условий элемента — только основные параметры его семейства
    // (плюс уже выбранная в строке, если её убрали из основных позже)
    characteristicOptionsFor(el, rule) {
      const allowed = el.main_characteristic_ids || [];
      return this.characteristicOptions.filter(
        (o) => allowed.includes(o.id) || o.id === rule.id_characteristic
      );
    },
    addNomenclatureRule(row) {
      this.addDraftRule({ idSchemeNodeElement: row.el.id, instance: row.n });
    },
    // Смена характеристики на тип «Выбор из справочника» с операндом,
    // отличным от =/≠ (например остался > от числовой характеристики) —
    // молча переводим на =, иначе условие тихо никогда не совпадёт.
    updateNomenclatureRule(uid, key, value) {
      this.updateRule({ uid, key, value });
      if (key === "id_characteristic" && this.isDictionaryCharacteristic(value)) {
        const rule = this.nomenclatureRules.find((r) => r.uid === uid);
        if (rule && rule.operand !== "eq" && rule.operand !== "neq") {
          this.updateRule({ uid, key: "operand", value: "eq" });
        }
      }
    },
    // Тип характеристики (d_type_characteristics.code) — определяет, чем
    // заполняется «Число»: числовой подсказкой из каталога или списком
    // значений справочника (см. isDictionaryCharacteristic).
    characteristicTypeCode(idCharacteristic) {
      const c = this.characteristicById(idCharacteristic);
      return c && c.type_characteristic ? c.type_characteristic.code : null;
    },
    isDictionaryCharacteristic(idCharacteristic) {
      return this.characteristicTypeCode(idCharacteristic) === DICTIONARY_TYPE_CODE;
    },
    // Для справочника имеет смысл только точное совпадение — сравнение
    // > < и т.д. со строкой не имеет физического смысла (см. также
    // серверную валидацию в MToMSchemeNodeNomenclatureRules::validate).
    operandOptionsFor(idCharacteristic) {
      return this.isDictionaryCharacteristic(idCharacteristic)
        ? this.operandOptions.filter((o) => o.value === "eq" || o.value === "neq")
        : this.operandOptions;
    },
    dictionaryIdFor(idCharacteristic) {
      const c = this.characteristicById(idCharacteristic);
      return c ? c.id_dictionary : null;
    },
    // Список значений справочника — канонический (d_dictionary_attributes),
    // а не то, что реально встречается в каталоге (в отличие от valuesFor).
    dictionaryValuesFor(idCharacteristic) {
      return this.dictionaryValuesById[this.dictionaryIdFor(idCharacteristic)] || [];
    },
    dictionaryValuesLoadingFor(idCharacteristic) {
      return Boolean(
        this.dictionaryValuesLoadingById[this.dictionaryIdFor(idCharacteristic)]
      );
    },
    loadDictionaryValuesFor(idCharacteristic) {
      const idDictionary = this.dictionaryIdFor(idCharacteristic);
      if (idDictionary) this.fetchDictionaryValues(idDictionary);
    },
    // Значения характеристики, реально встречающиеся в каталоге — подсказка
    // в поле «Число». Грузится лениво по фокусу, а не для всех строк сразу.
    valuesFor(idCharacteristic) {
      return this.valuesByCharacteristic[idCharacteristic] || [];
    },
    valuesLoadingFor(idCharacteristic) {
      return Boolean(this.valuesLoadingByCharacteristic[idCharacteristic]);
    },
    loadValuesFor(idCharacteristic) {
      if (idCharacteristic) this.fetchCharacteristicValues(idCharacteristic);
    },
    removeNomenclatureRule(uid) {
      // строка экземпляра остаётся в таблице и после удаления последнего условия — иначе она
      // исчезла бы из-под рук, пока админ правит условия; убрать её можно кнопкой в самой строке
      const rule = this.nomenclatureRules.find((r) => r.uid === uid);
      if (rule && rule.id_scheme_node_element && rule.element_instance) {
        const key = this.equipmentKey(rule.id_scheme_node_element, rule.element_instance);
        if (!this.addedEquipmentKeys.includes(key)) {
          this.addedEquipmentKeys = [...this.addedEquipmentKeys, key];
        }
      }
      this.removeRule(uid);
    },
    // Ключ экземпляра оборудования: «id_элемента-номер» (КШ-2 → «1-2»)
    equipmentKey(idElement, n) {
      return `${idElement}-${n}`;
    },
    // Условия конкретного экземпляра (строка в таблице «Оборудование»)
    rowRules(row) {
      return this.nomenclatureRules.filter(
        (r) => r.id_scheme_node_element === row.el.id && r.element_instance === row.n
      );
    },
    // icon — автономная data:URI, собранная бэком прямо из <symbol> этого SVG
    // (см. SchemeNodeElementsDetector): как элемент реально нарисован на узле.
    // _file — иконка, вручную загруженная в справочник элементов; нужна, если в SVG
    // нет символа этого элемента (экспорт потерял его, например Pump) и icon пуст.
    elementIconSrc(el) {
      return el.icon || (el._file && el._file.url) || undefined;
    },
    async loadFunctionalities(id) {
      this.functionalitiesLoading = true;
      const items = await this.getFunctionalities(id);
      if (this.entry.id !== id) return;
      this.functionalities = items;
      this.functionalitiesLoading = false;
    },
    startFunctionalityDraft() {
      this.functionalityDraft = {
        id: null,
        text: "",
        position: this.functionalities.length,
        element_ids: [],
        element_refs: [],
      };
      this.functionalitySelection = null;
      this.hoveredFunctionalityRefs = [];
    },
    editFunctionality(item) {
      this.functionalityDraft = {
        id: item.id,
        text: item.text || "",
        position: item.position || 0,
        element_ids: [...new Set((item.element_ids || []).map(Number))],
        element_refs: this.functionalityRefs(item),
      };
      this.functionalitySelection = null;
    },
    cancelFunctionalityDraft() {
      this.functionalityDraft = null;
      this.functionalitySelection = null;
      this.hoveredFunctionalityRefs = [];
    },
    functionalityParts(item) {
      return functionalityTextParts(item.text);
    },
    rememberFunctionalitySelection(range) {
      // Quill emits null on blur when the user clicks the SVG; keep the last caret/range.
      if (range) this.functionalitySelection = { index: range.index, length: range.length };
    },
    captureFunctionalityCaret() {
      if (this.svgInfoTab !== 1 || !this.functionalityDraft) return;
      const editor = this.$refs.functionalityEditor && this.$refs.functionalityEditor.quill;
      const range = editor && editor.getSelection();
      if (range) this.functionalitySelection = { index: range.index, length: range.length };
    },
    syncFunctionalityLinks() {
      if (!this.functionalityDraft) return;
      const editor = this.$refs.functionalityEditor && this.$refs.functionalityEditor.quill;
      const html = editor ? editor.root.innerHTML : this.functionalityDraft.text;
      this.functionalityDraft.text = html;
      this.functionalityDraft.element_refs = linkedElementRefs(html);
      this.functionalityDraft.element_ids = linkedElementIds(html);
      this.hoveredFunctionalityRefs = [...this.functionalityDraft.element_refs];
    },
    functionalityRefs(item) {
      const refsInText = linkedElementRefs(item.text || "");
      if (refsInText.length) return refsInText;
      if (item.element_refs && item.element_refs.length) {
        return item.element_refs.map((ref) => ({ id: Number(ref.id), instance: ref.instance == null ? null : Number(ref.instance) }));
      }
      return (item.element_ids || []).map((id) => ({ id: Number(id), instance: null }));
    },
    functionalityElements(item) {
      return this.functionalityRefs(item).map((ref) => {
        const element = this.detectedElements.find((candidate) => candidate.id === Number(ref.id)) || {
          id: Number(ref.id),
          code: `#${ref.id}`,
          name_ru: `Элемент легенды #${ref.id}`,
        };
        const prefix = element.marking_ru || element.name_ru || element.code;
        return { ...ref, ...element, label: ref.instance == null ? prefix : `${prefix}-${ref.instance}` };
      });
    },
    setHoveredFunctionality(item) {
      this.hoveredFunctionalityRefs = item ? this.functionalityRefs(item) : [];
    },
    isFunctionalityHighlighted(key) {
      const [idElement, instance] = String(key).split("-").map(Number);
      const refs = this.hoveredFunctionalityRefs.length
        ? this.hoveredFunctionalityRefs
        : (this.functionalityDraft && this.functionalityDraft.element_refs) || [];
      return refs.some((ref) => Number(ref.id) === idElement && (ref.instance == null || Number(ref.instance) === instance));
    },
    removeFunctionalityElement(elementRef) {
      const editor = this.$refs.functionalityEditor && this.$refs.functionalityEditor.quill;
      if (!editor) return;
      const idElement = Number(elementRef.id);
      const instance = elementRef.instance == null ? null : Number(elementRef.instance);
      const target = `#scheme-node-element-${idElement}${instance == null ? "" : `-instance-${instance}`}`;
      let position = 0;
      const matches = [];
      editor.getContents().ops.forEach((op) => {
        const length = typeof op.insert === "string" ? op.insert.length : 1;
        const link = op.attributes && op.attributes.link;
        const legacyInstanceLink = instance != null && link === `#scheme-node-element-${idElement}` &&
          typeof op.insert === "string" && op.insert.trim().endsWith(`-${instance}`);
        if (link === target || legacyInstanceLink) matches.push({ position, length });
        position += length;
      });
      matches.reverse().forEach(({ position: start, length }) => {
        editor.formatText(start, length, "link", false, "user");
      });
      this.syncFunctionalityLinks();
    },
    onSvgElementClick(el, inst) {
      if (this.svgInfoTab === 1 && this.functionalityDraft) {
        this.insertFunctionalityLink(el, inst);
        return;
      }
      this.selectEquipment(el, inst);
    },
    insertFunctionalityLink(el, inst) {
      const editor = this.$refs.functionalityEditor && this.$refs.functionalityEditor.quill;
      if (!editor) return;
      const current = this.functionalitySelection || editor.getSelection();
      const end = Math.max(0, editor.getLength() - 1);
      const range = current
        ? {
            index: Math.min(current.index, end),
            length: Math.min(current.length, Math.max(0, end - current.index)),
          }
        : { index: end, length: 0 };
      const href = `#scheme-node-element-${el.id}${inst ? `-instance-${inst.n}` : ""}`;

      if (range.length) {
        editor.formatText(range.index, range.length, "link", href, "user");
        editor.setSelection(range.index + range.length, 0, "silent");
        editor.format("link", false, "silent");
        this.functionalitySelection = { index: range.index + range.length, length: 0 };
      } else {
        const label =
          (inst && inst.label) ||
          el.marking_ru ||
          el.name_ru ||
          el.code ||
          `Элемент ${el.id}`;
        editor.insertText(range.index, label, "link", href, "user");
        editor.setSelection(range.index + label.length, 0, "silent");
        editor.format("link", false, "silent");
        this.functionalitySelection = { index: range.index + label.length, length: 0 };
      }
      this.syncFunctionalityLinks();
      this.hoveredFunctionalityRefs = [...this.functionalityDraft.element_refs];
    },
    async persistFunctionality() {
      if (!this.functionalityDraft || !this.entry.id) return;
      this.syncFunctionalityLinks();
      if (!this.functionalityDraft.element_refs.length) return;
      this.functionalitySaving = true;
      try {
        const saved = await this.saveFunctionality({
          idNode: this.entry.id,
          functionality: this.functionalityDraft,
        });
        if (!saved || this.entry.id !== saved.id_scheme_node) return;
        const index = this.functionalities.findIndex((item) => item.id === saved.id);
        if (index === -1) this.functionalities.push(saved);
        else this.$set(this.functionalities, index, saved);
        this.functionalities.sort((a, b) => a.position - b.position || a.id - b.id);
        this.cancelFunctionalityDraft();
      } finally {
        this.functionalitySaving = false;
      }
    },
    async removeFunctionality(item) {
      const removed = await this.deleteFunctionality({ idNode: this.entry.id, id: item.id });
      if (removed) {
        this.functionalities = this.functionalities.filter((entry) => entry.id !== item.id);
        if (this.functionalityDraft && this.functionalityDraft.id === item.id) {
          this.cancelFunctionalityDraft();
        }
      }
    },
    // Есть ли у экземпляра сохранённое условие — иначе подбирать не по чему
    equipmentHasSavedRules(row) {
      return !row.missing && this.rowRules(row).some((rule) => rule.id);
    },
    // Лупа в строке «Оборудования»: номенклатура по условиям только этого экземпляра
    openEquipmentCandidates(row) {
      if (!this.equipmentHasSavedRules(row)) return;
      // Колонки таблицы — характеристики его сохранённых условий (их же берёт бэкенд)
      this.candidateCharacteristicIds = this.rowRules(row)
        .filter((rule) => rule.id)
        .map((rule) => rule.id_characteristic);
      this.candidatesRowKey = row.key;
      this.candidatesDialog = true;
      this.fetchCandidates([`${row.el.id}:${row.n}`]);
    },
    // «DN ≥ 20» — условие для плашки в модалке
    candidateRuleText(rule) {
      const char = this.nomenclatureCharacteristicsRaw.find((c) => c.id === rule.id_characteristic);
      const operand = this.operandOptions.find((o) => o.value === rule.operand);
      const postfix = char && char.postfix ? ` ${char.postfix}` : "";
      return `${char ? char.name : `#${rule.id_characteristic}`} ${operand ? operand.label : rule.operand} ${rule.value}${postfix}`;
    },
    formatCandidateValue(item, char) {
      const raw = item.values ? item.values[char.id] : null;
      if (raw === null || raw === undefined) return "—";
      const num = Number(raw);
      const formatted = Number.isFinite(num) ? num : raw;
      return char.postfix ? `${formatted} ${char.postfix}` : `${formatted}`;
    },
    hasChildren(item) {
      return Boolean(item.children && item.children.length);
    },
    // Ширина панели, в которой стоит превью (колонки), — для ширины самого превью
    measurePanelWidth() {
      this.windowHeight = window.innerHeight;
      const el = this.$refs.panelCols;
      if (el && el.clientWidth) this.panelWidth = el.clientWidth;
    },
    // Код трубы у точки, сохранённой без него (посчитана до того, как детектор стал отдавать
    // pipe): ищем ту же точку в свежем разборе SVG
    detectedPipeCodeAt(side, point) {
      const fresh = (this.detectedAnchorPipes && this.detectedAnchorPipes[side]) || [];
      const match = fresh.find(
        (p) => p.pipe && Math.hypot(p.x - point.x, p.y - point.y) <= ANCHOR_PIPE_MATCH_DISTANCE
      );
      return match ? match.pipe : null;
    },
    // Подсказка ячейки таблицы труб: «ОП — слева» (и сколько коннекторов, если их несколько)
    anchorCellTitle(row, side) {
      const count = row.counts[side.key];
      return `${row.label} — ${side.label.toLowerCase()}${count > 1 ? `, коннекторов: ${count}` : ""}`;
    },
    // viewBox парсится на бэке (см. DSchemeNodes::getSvgViewboxAttribute) и
    // отдаётся через API — сам SVG отдаётся напрямую веб-сервером в обход
    // Laravel, так что fetch() по ссылке на файл с другого домена падает по
    // CORS. Дожидаться этого не нужно, id узла уже известен на этот момент.
    async loadSvgViewBox() {
      this.svgViewBox = null;
      this.svgViewBoxError = false;
      if (!this.entry.id || !this.entry._file || !this.entry._file.url) return;

      this.svgViewBoxLoading = true;
      try {
        const box = await this.getSvgViewbox(this.entry.id);
        if (box) {
          this.measurePanelWidth();
          this.svgViewBox = box;
        } else {
          this.svgViewBoxError = true;
        }
      } finally {
        this.svgViewBoxLoading = false;
      }
    },
    // _detected_elements грузится отдельным запросом (как _svg_viewbox) —
    // не приходит вместе с деревом узлов.
    async loadDetectedElements() {
      this.detectedElements = [];
      this.detectedVariants = [1];
      this.selectedVariant = 1;
      this.setHoveredEquipment(null);
      this.addedEquipmentKeys = [];
      this.expandedEquipmentKeys = [];
      if (!this.entry.id) return;

      const id = this.entry.id;
      const detected = await this.getDetectedElements(id);
      // за время запроса диалог мог открыться уже для другого узла
      if (this.entry.id !== id) return;
      this.detectedElements = detected.elements || [];
      this.detectedVariants = detected.variants || [1];
      this.preloadVariantImages();
    },
    // Экземпляр виден в выбранном варианте: свой или общий для всех (variant = null)
    isInstanceInVariant(inst) {
      return inst.variant == null || inst.variant === this.selectedVariant;
    },
    // Адрес картинки узла для варианта: без вариантов — сам файл, с вариантами — чертёж одного варианта с бэка
    variantImageUrl(variant) {
      const file = this.entry._file;
      if (!file || !file.url) return "";
      if (!this.hasVariants) return file.url;
      return `${this.$store.state.BASE_URL}/dictionary/scheme-nodes/${this.entry.id}/svg?variant=${variant}&v=${this.entry.id_file}`;
    },
    // Картинки остальных вариантов подгружаются заранее: переключение без паузы
    preloadVariantImages() {
      if (!this.hasVariants) return;
      this.detectedVariants.forEach((variant) => {
        new Image().src = this.variantImageUrl(variant);
      });
    },
    // Сколько строк в «Оборудовании» у варианта — для подписи на переключателе
    variantRowCount(variant) {
      const keys = new Set(this.addedEquipmentKeys);
      this.nomenclatureRules.forEach((r) => {
        if (r.id_scheme_node_element && r.element_instance) {
          keys.add(this.equipmentKey(r.id_scheme_node_element, r.element_instance));
        }
      });
      let count = 0;
      this.detectedElements.forEach((el) => {
        el.instances.forEach((inst) => {
          if ((inst.variant == null || inst.variant === variant) && keys.has(this.equipmentKey(el.id, inst.n))) count++;
        });
      });
      return count;
    },
    // Переключение варианта сборки: картинка и таблица показывают его экземпляры; наведение сбрасываем
    selectVariant(variant) {
      if (!variant || variant === this.selectedVariant) return;
      this.setHoveredEquipment(null);
      this.selectedVariant = variant;
    },
    // Трубы справочника на SVG узла и свежий разбор точек с кодом трубы — отдельным запросом,
    // как viewBox и элементы: вместе с деревом узлов не приходят.
    async loadDetectedPipes() {
      this.detectedPipes = [];
      this.detectedAnchorPipes = null;
      this.detectedPipesLoading = false;
      this.setHoveredPipe(null);
      if (!this.entry.id || !this.entry._file || !this.entry._file.url) return;

      const id = this.entry.id;
      this.detectedPipesLoading = true;
      try {
        const { pipes, anchors } = await this.getDetectedPipes(id);
        // за время запроса диалог мог переключиться на другой узел — тогда ответ чужой
        if (this.entry.id !== id) return;
        this.detectedPipes = pipes;
        this.detectedAnchorPipes = anchors;
      } finally {
        if (this.entry.id === id) this.detectedPipesLoading = false;
      }
    },
    // Наведение на экземпляр оборудования: с SVG (fromSvg) или со строки таблицы; без ключа — сброс
    setHoveredEquipment(key, fromSvg = false) {
      this.hoveredEquipmentKey = key || null;
      this.hoveredEquipmentFromSvg = !!key && fromSvg;
    },
    // Наведение на трубу: с SVG (fromSvg) или со строки таблицы; без аргумента — сброс
    setHoveredPipe(code, fromSvg = false) {
      this.hoveredPipeCode = code || null;
      this.hoveredPipeFromSvg = !!code && fromSvg;
    },
    // Строку «Коннекторов» красим синим только когда труба под курсором на SVG — как у оборудования
    isPipeRowHovered(row) {
      return this.hoveredPipeFromSvg && this.hoveredPipeCode === row.key;
    },
    // Строку в «Оборудовании» красим синим только когда экземпляр под курсором на SVG —
    // обычное наведение на саму строку не подсвечивает её целиком
    isEquipmentRowHovered(key) {
      return this.hoveredEquipmentFromSvg && this.hoveredEquipmentKey === key;
    },
    // Другой экземпляр того же типа, что и тот, что под курсором (при наведении на КШ-1 это
    // КШ-2, КШ-3…): подсвечивается так же ярко и толсто, но жёлтым. Ключ — «id_элемента-номер».
    isEquipmentSibling(key) {
      const hovered = this.hoveredEquipmentKey;
      return !!hovered && hovered !== key && hovered.split("-")[0] === key.split("-")[0];
    },
    isEquipmentHighlighted(key) {
      return (
        this.hoveredEquipmentKey === key ||
        this.isEquipmentSibling(key) ||
        this.isFunctionalityHighlighted(key) ||
        this.isEquipmentExpanded(key)
      );
    },
    // рамка на SVG: экземпляр под курсором («текущий») — фиолетовая, раскрытая строка — красная, другие
    // экземпляры того же типа — золотая (одинаково толстая), уже добавленный в оборудование — сплошная
    // тёмно-серая, остальные — пунктир
    equipmentStrokeColor(key) {
      if (this.hoveredEquipmentKey === key) {
        return EQUIPMENT_HOVER_COLOR;
      }
      if (this.isEquipmentExpanded(key)) {
        return EQUIPMENT_EXPANDED_COLOR;
      }
      if (this.isFunctionalityHighlighted(key)) {
        return "#00838F";
      }
      if (this.isEquipmentSibling(key)) {
        return EQUIPMENT_SIBLING_COLOR;
      }
      return this.isInEquipment(key) ? "#757575" : "#9e9e9e";
    },
    // заливка под рамкой на SVG: экземпляр под курсором и остальные того же типа; у прочих её нет
    equipmentWash(key) {
      if (this.hoveredEquipmentKey === key) return EQUIPMENT_HOVER_WASH;
      if (this.isFunctionalityHighlighted(key)) return "rgba(0, 150, 136, 0.28)";
      if (this.isEquipmentSibling(key)) return EQUIPMENT_SIBLING_WASH;
      return null;
    },
    isInEquipment(key) {
      return this.equipmentKeys.has(key);
    },
    isEquipmentExpanded(key) {
      return this.expandedEquipmentKeys.includes(key);
    },
    // v-select для «Значение»/«Число» может показать текст уже выбранного
    // значения только если оно есть в :items — а тот грузится лениво по
    // фокусу. Раз строка раскрывается со своими уже сохранёнными условиями,
    // подгружаем сразу, иначе выбранное значение будет казаться пустым, пока
    // админ не кликнет в поле сам.
    preloadValuesForRow(row) {
      this.rowRules(row).forEach((rule) => {
        if (!rule.id_characteristic) return;
        if (this.isDictionaryCharacteristic(rule.id_characteristic)) {
          this.loadDictionaryValuesFor(rule.id_characteristic);
        } else {
          this.loadValuesFor(rule.id_characteristic);
        }
      });
    },
    // Раскрыть/свернуть условия строки в таблице «Оборудование» — независимо от остальных
    // строк, несколько могут быть открыты одновременно.
    toggleExpandedEquipment(row) {
      if (this.isEquipmentExpanded(row.key)) {
        this.expandedEquipmentKeys = this.expandedEquipmentKeys.filter((k) => k !== row.key);
      } else {
        this.expandedEquipmentKeys = [...this.expandedEquipmentKeys, row.key];
        this.preloadValuesForRow(row);
      }
    },
    // Клик по экземпляру на SVG: добавляет его строку в «Оборудование» (если её там ещё нет) и
    // раскрывает; повторный клик по раскрытому сворачивает. Красная рамка на SVG и раскрытая
    // строка — это одно состояние (expandedEquipmentKeys). Все условия уже загружены разом в
    // fetchRules, новый запрос не нужен.
    selectEquipment(el, inst) {
      const key = this.equipmentKey(el.id, inst.n);
      if (!this.isInEquipment(key)) {
        this.addedEquipmentKeys = [...this.addedEquipmentKeys, key];
      }
      if (this.isEquipmentExpanded(key)) {
        this.expandedEquipmentKeys = this.expandedEquipmentKeys.filter((k) => k !== key);
        return;
      }
      this.expandedEquipmentKeys = [...this.expandedEquipmentKeys, key];
      this.preloadValuesForRow({ key, el, n: inst.n });
      // таблица ниже картинки — подводим к новой строке
      this.$nextTick(() => {
        const node = document.getElementById(`eq-row-${key}`);
        if (node && node.scrollIntoView) node.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
    // Убрать строку без условий из «Оборудования»
    removeEquipmentRow(row) {
      this.addedEquipmentKeys = this.addedEquipmentKeys.filter((k) => k !== row.key);
      this.expandedEquipmentKeys = this.expandedEquipmentKeys.filter((k) => k !== row.key);
      if (this.hoveredEquipmentKey === row.key) this.setHoveredEquipment(null);
    },
    async detectAnchors() {
      if (!this.entry.id) return;
      this.loading = true;
      try {
        const response = await this.detectAnchorsForNode(this.entry.id);
        if (response && !response.isError && response.data) {
          this.entry.anchors = response.data.anchors;
        }
      } finally {
        this.loading = false;
      }
    },
    // Держим ?open=<id> в URL синхронным с открытым диалогом — ссылку на
    // конкретный узел (например из «Правил соответствия узлов») можно
    // скопировать в любой момент, а не только сразу после перехода по ней.
    syncOpenQuery(id) {
      const current = this.$route.query.open;
      const next = id ? String(id) : undefined;
      if (current === next) return;

      const query = { ...this.$route.query };
      if (next) {
        query.open = next;
      } else {
        delete query.open;
      }

      this.$router.replace({ query }).catch((err) => {
        if (err && err.name !== "NavigationDuplicated") {
          console.error("scheme-nodes-tree: не удалось синхронизировать URL", err);
        }
      });
    },
    openCreateDialog(parentItem) {
      this.clearEntry();
      this.entry.id_parent = parentItem ? parentItem.id : null;
      this.parentCode = parentItem ? parentItem.code : "";
      this.saveErrorMessage = "";
      this.isEditing = false;
      this.notePanelIndex = [];
      this.seoPanelIndex = [];
      this.clearRules();
      this.functionalities = [];
      this.functionalityDraft = null;
      this.hoveredFunctionalityRefs = [];
      this.svgInfoTab = 0;
      this.detectedElements = [];
      this.detectedVariants = [1];
      this.selectedVariant = 1;
      this.detectedPipes = [];
      this.detectedAnchorPipes = null;
      this.detectedPipesLoading = false;
      this.setHoveredPipe(null);
      this.setHoveredEquipment(null);
      this.addedEquipmentKeys = [];
      this.expandedEquipmentKeys = [];
      this.dialog = true;
      this.syncOpenQuery(null);
      this.focusCodeField();
    },
    // Фокус в поле «Код» при создании узла. v-dialog сам переводит фокус на свой контейнер уже после показа
    // (и лениво рисует содержимое при первом открытии), поэтому ставим фокус с небольшой задержкой
    focusCodeField() {
      setTimeout(() => {
        const field = this.$refs.codeField;
        if (this.dialog && !this.isEditing && field) field.focus();
      }, 200);
    },
    openEditDialog(item) {
      this.saveErrorMessage = "";
      this.setEntry(
        new SchemeNode(
          item.id,
          item.id_parent,
          item.is_folder,
          item.code,
          item.id_file,
          item.anchors,
          item._file,
          item.color,
          item.name_en,
          item.name_ru,
          item.description_en,
          item.description_ru,
          item.is_published,
          item.seo_title,
          item.seo_description,
          item.seo_keywords
        )
      );
      this.parentCode = "";
      this.isEditing = true;
      this.notePanelIndex = [];
      this.seoPanelIndex = [];
      this.expandedPanels = [0];
      this.functionalities = [];
      this.functionalityDraft = null;
      this.hoveredFunctionalityRefs = [];
      this.svgInfoTab = 0;
      this.dialog = true;
      this.syncOpenQuery(item.id);
      this.usage = null;
      this.usageOpen = false;
      if (!item.is_folder) {
        const id = item.id;
        this.getUsage(id).then((usage) => {
          // за время запроса диалог мог открыться уже для другого узла
          if (this.entry.id === id) this.usage = usage;
        });
      }
      this.loadFunctionalities(item.id);
      this.loadSvgViewBox();
      this.loadDetectedPipes();
      this.loadDetectedElements();
      if (!item.is_folder) {
        this.fetchRules(item.id);
      } else {
        this.clearRules();
      }
    },
    // Открытие узла по ?open=<id> в URL — используется, например, из
    // «Правил соответствия узлов» (иконка рядом с кодом центрального узла).
    findNodeById(nodes, id) {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = this.findNodeById(node.children, id);
          if (found) return found;
        }
      }
      return null;
    },
    openFromQuery() {
      const id = Number(this.$route.query.open);
      if (!id) return;
      const node = this.findNodeById(this.tree, id);
      if (node) this.openEditDialog(node);
    },
    // Обратная навигация к «Открыть узел в Узлах схемы» из SchemeNodeRules.vue
    openInSchemeNodeRules() {
      if (!this.entry.code) return;
      const routeData = this.$router.resolve({
        path: `/scheme-node-rules/${encodeURIComponent(this.entry.code.toLowerCase())}`,
      });
      window.open(routeData.href, "_blank");
    },
    async saveEntry() {
      if (!this.entry.code) return;

      let response;
      if (this.isEditing) {
        response = await this.updateEntry(this.entry);
      } else {
        response = await this.createEntry(this.entry);
      }

      if (response && response.isError) {
        this.saveErrorMessage = response.message;
        return;
      }

      this.saveErrorMessage = "";
      this.dialog = false;
      this.clearEntry();
    },
    openDeleteDialog(item) {
      this.setEntry(
        new SchemeNode(
          item.id,
          item.id_parent,
          item.is_folder,
          item.code,
          item.id_file,
          item.anchors,
          item._file,
          item.color,
          item.name_en,
          item.name_ru,
          item.description_en,
          item.description_ru,
          item.is_published,
          item.seo_title,
          item.seo_description,
          item.seo_keywords
        )
      );
      this.deleteModalCommit(true);
    },
    async confirmDelete() {
      await this.deleteEntry(this.entry.id);
      this.clearEntry();
    },
    triggerFileInput(item) {
      this.uploadTargetNode = item;
      this.$refs.fileInput.click();
    },
    findNodeInTree(nodes, id) {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = this.findNodeInTree(node.children, id);
          if (found) return found;
        }
      }
      return null;
    },
    async onFileSelected(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file || !this.uploadTargetNode) return;

      const targetId = this.uploadTargetNode.id;
      await this.uploadSvgForNode({ node: this.uploadTargetNode, file });
      this.uploadTargetNode = null;

      if (this.dialog && this.entry.id === targetId) {
        const freshNode = this.findNodeInTree(this.tree, targetId);
        if (freshNode) {
          this.setEntry(
            new SchemeNode(
              freshNode.id,
              freshNode.id_parent,
              freshNode.is_folder,
              freshNode.code,
              freshNode.id_file,
              freshNode.anchors,
              freshNode._file,
              freshNode.color,
              freshNode.name_en,
              freshNode.name_ru,
              freshNode.description_en,
              freshNode.description_ru,
              freshNode.is_published,
              freshNode.seo_title,
              freshNode.seo_description,
              freshNode.seo_keywords
            )
          );
          this.loadSvgViewBox();
          this.loadDetectedPipes();
          this.loadDetectedElements();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// строка «значок публикации + превью/иконка»: по центру, в одну линию
.node-prepend {
  display: flex;
  align-items: center;
  height: 100%;
}
// увеличенное превью узла (следует за курсором, не перехватывает мышь)
.node-hover-preview {
  position: fixed;
  z-index: 210;
  width: 300px;
  height: 380px;
  padding: 8px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  pointer-events: none;
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
// место под значок «не опубликован» слева от превью узла
.node-publish-slot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  margin-right: 2px;
}
.usage-scheme-link {
  color: inherit;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.scheme-node-functionalities {
  min-height: 88px;
}
.scheme-node-svg-tabs {
  ::v-deep .v-tab {
    min-width: 0;
    padding: 0 6px;
    font-size: 12px;
    line-height: 1.1;
    text-align: center;
    white-space: normal;
  }
}
.scheme-node-functionality {
  margin-bottom: 6px;
  padding: 8px 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background: #fafafa;
  transition: background-color 120ms ease, border-color 120ms ease;
  &:hover {
    border-color: #00838f;
    background: #e0f2f1;
  }
  &__text {
    flex: 1 1 auto;
    min-width: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  &__editor {
    ::v-deep .ql-container {
      height: 112px;
    }
    ::v-deep .ql-editor {
      min-height: 70px;
      max-height: 112px;
      overflow-y: auto;
    }
  }
  &__actions {
    position: sticky;
    bottom: 0;
    z-index: 2;
    padding-top: 8px;
    background: #fff;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
  &__link {
    color: #006064;
    text-decoration: underline;
    text-decoration-style: dotted;
  }
}

.scheme-nodes-tree {
  padding: 15px;
}

// Vuetify по умолчанию подсвечивает строки v-simple-table при наведении —
// убираем именно в «Оборудовании» (просили не подсвечивать строку целиком).
// Раскрытую строку это не касается — у неё свой постоянный фон (см. ниже).
::v-deep .equipment-table tbody tr:hover:not(.eq-row--expanded) {
  background: transparent !important;
}

// Таблица труб только показывает результат — строки при наведении не подсвечиваем
::v-deep .anchor-pipes-table tbody tr:hover {
  background: transparent !important;
}

// Название трубы начинается с цветного штриха-образца, закрашенная ячейка — плашка цвета
// трубы; тонкая рамка нужна, чтобы светлые цвета не терялись на белом
.anchor-pipes-table__swatch {
  display: inline-block;
  width: 14px;
  height: 4px;
  margin-right: 8px;
  border-radius: 2px;
  vertical-align: middle;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
}

// Столбцы сторон одинаковой ширины, даже когда в них нет ни одной плашки. Тонкая линия слева у
// каждого — внутренние вертикальные линии сетки (горизонтальные между строками рисует сам
// Vuetify), вместе они разбивают таблицу на ячейки «труба × сторона»
.anchor-pipes-table__side {
  min-width: 40px;
  border-left: thin solid rgba(0, 0, 0, 0.12);
}

.anchor-pipes-table__fill {
  width: 28px;
  height: 20px;
  margin: 0 auto;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
}

// Раскрытая строка элемента + её условия ниже — единый визуальный блок:
// общий фон, без разделительной линии между шапкой и условиями (только
// снизу самого блока условий, отделяя его от следующего элемента).
.eq-row--expanded,
.eq-expand-row {
  background-color: #fafafa;
}

.eq-row--expanded ::v-deep > td {
  border-bottom: none !important;
}

// Заголовки блоков панели «SVG, коннекторы и оборудование» («SVG», «Коннекторы»,
// «Оборудование») — один размер и одна толщина, задаются только здесь
.node-section-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: 0.1px;
  color: rgba(0, 0, 0, 0.6);

  // счётчик и значки справки — того же цвета, что и сам заголовок
  ::v-deep .v-icon {
    color: inherit !important;
  }
}

// Элемент под курсором на SVG: его строка в «Оборудовании» (и раскрытые условия под ней)
// подсвечена тем же фиолетовым, что и рамка на картинке (equipmentStrokeColor, #7B1FA2); строка трубы
// в «Коннекторах», когда труба под курсором на SVG, — синим, как сама труба на картинке.
// Правило ниже раскрытой строки — при равной специфичности оно перекрывает её серый фон.
::v-deep .equipment-table tbody tr,
::v-deep .anchor-pipes-table tbody tr {
  transition: background-color 0.12s;
}

.eq-row--hovered {
  background-color: rgba(25, 118, 210, 0.1);
}

// текущий экземпляр оборудования (под курсором на SVG) — тот же фиолетовый, что и его рамка
.eq-row--current {
  background-color: rgba(123, 31, 162, 0.08);
}

.node-label-row {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  height: 100%;
  cursor: pointer;
}

.node-label-box {
  display: flex;
  align-items: center;
  min-width: 0;
}

.node-label-box--colored {
  border: 1.5px solid;
  border-radius: 4px;
  padding: 2px 10px;
}

.node-label-leader {
  flex: 1 1 auto;
  min-width: 12px;
  height: 0;
  margin: 0 8px;
  align-self: center;
  border-bottom: 2px dotted #9e9e9e;
}

// Диалог узла: у v-card-text в v-dialog padding-top: 0 — при scrollable
// плавающий лейбл первого поля ("Код") обрезается верхним краем области
// прокрутки. Добавляем зазор только сверху, остальной padding не трогаем.
::v-deep .node-dialog .v-card__text {
  padding-top: 12px;
}

// Текст во всех полях и подписях диалога узла — дефолтный размер Vuetify
// (16px) ощущался слишком крупным на такой плотной форме, но 12-13px мелко.
::v-deep .node-dialog {
  .v-label,
  input,
  textarea,
  .v-select__selection {
    font-size: 14px;
  }

  // .text-caption у Vuetify задан с !important
  .text-caption {
    font-size: 13px !important;
  }

  .v-chip.v-size--small {
    font-size: 13px;
  }
}
</style>

<!-- Не scoped: содержимое подсказки Vuetify выносит в корень приложения, за пределы компонента -->
<style lang="scss">
// Подсказки диалога узла: по умолчанию у Vuetify фон полупрозрачный (rgba .9 да ещё opacity .9),
// и сквозь него просвечивает таблица под ним — делаем фон непрозрачным
.v-tooltip__content.menuable__content__active.node-dialog-tooltip {
  background: #424242;
  opacity: 1;
}
</style>
